import React, { useEffect, useState, useMemo, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import { showAlertFailure } from 'client/methods'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AuthFormFooter from 'components/auth/AuthFormFooter'
import AuthTitle from 'components/internal/AuthTitle'
import Button from 'components/buttons/Button'
import Card from 'components/cards/Card'
import Column from 'components/base/Column'
import copyTextToClipBoard from 'lib/copyTextToClipboard'
import FontIcon from 'components/base/FontIcon'
import Loader from 'components/loaders/Loader'
import Row from 'components/base/Row'
import Stack from 'components/base/Stack'
import Text from 'components/typography/Text'
import TextInput from 'components/inputs/TextInput'
import useMutation from 'hooks/useMutation'
import {
  BEGIN_MFA_ENROLLMENT_MUTATION,
  COMPLETE_MFA_ENROLLMENT_MUTATION
} from 'models/MFA'

const useStyles = createUseStyles(({ colors }) => ({
  qrContainer: {
    height: 180,
    width: 200,
    borderRadius: 10,
    background: colors.light,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  qrImage: {
    height: 150,
    width: 150
  },
  pointer: {
    cursor: 'pointer',
    position: 'relative'
  },
  copied: {
    marginLeft: 8,
    fontSize: 12,
    color: colors.tertiary,
    opacity: 1,
    animation: '$fadeOut 1.2s forwards'
  },
  '@keyframes fadeOut': {
    '0%': { opacity: 1 },
    '100%': { opacity: 0 }
  }
}))

function QrCodeImage({ svg }) {
  const classes = useStyles()
  const src = useMemo(() => {
    if (!svg) return null
    const blob = new Blob([svg], { type: 'image/svg+xml' })
    return URL.createObjectURL(blob)
  }, [svg])

  useEffect(
    () => () => {
      if (src) {
        URL.revokeObjectURL(src)
      }
    },
    [src]
  )

  if (!src) return null

  return (
    <div className={classes.qrContainer}>
      <img
        src={src}
        alt="QR code for authenticator app"
        className={classes.qrImage}
      />
    </div>
  )
}

function MfaAuthenticatorSetupPage() {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const inputRef = useRef(null)

  const account = state?.account
  const session = state?.session

  const [code, setCode] = useState('')
  const [qr, setQr] = useState(null)
  const [secret, setSecret] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!account || !session) {
      history.replace('/login')
    }
  }, [account, session, history])

  const [beginMfaEnrollment] = useMutation(BEGIN_MFA_ENROLLMENT_MUTATION)

  const [completeEnrollment, { loading }] = useMutation(
    COMPLETE_MFA_ENROLLMENT_MUTATION,
    {
      onCompleted: ({ completeMfaEnrollment: newSession }) => {
        history.push({
          pathname: '/mfa/backup',
          state: {
            account,
            session: newSession
          }
        })
      },
      onError: () => {
        showAlertFailure({
          message: t('mfa.verify.invalidCode')
        })
        setCode('')
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    }
  )

  useEffect(() => {
    if (!session) return

    beginMfaEnrollment({
      accessToken: session?.accessToken
    })
      .then(res => {
        const { qrSvg, plainSecret } = res.data.beginMfaEnrollment
        setQr(qrSvg)
        setSecret(plainSecret)
      })
      .catch(() => {
        showAlertFailure({
          message: t('mfa.errors.unableToStart')
        })
      })
  }, [session, t])

  const handleConfirm = () => {
    if (loading) return
    if (!code.trim() || code.length !== 6) return

    completeEnrollment({
      accessToken: session.accessToken,
      code
    })
  }

  const handleCopy = () => {
    if (!secret) return
    copyTextToClipBoard(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 1200)
  }

  if (!qr) return <Loader />

  return (
    <>
      <AuthTitle
        title={t('mfa.authenticator.title')}
        subtitle={`${t('mfa.authenticator.subtitle1')} ${t(
          'mfa.authenticator.subtitle2'
        )}`}
        subtitlePosition="below"
      />

      <Row>
        <Column span={3} hiddenMd hiddenXs />
        <Column span={6}>
          <Card variant="secondary">
            <form
              onSubmit={e => {
                e.preventDefault()
                handleConfirm()
              }}
            >
              <Stack direction="column" align="flex-start" gutter={39} stretch>
                <Stack align="flex-start">
                  <QrCodeImage svg={qr} />
                  <Stack direction="column" align="flex-start">
                    <div>
                      <Text weight="medium" size="small" color="light">
                        {t('mfa.authenticator.step1Title')}
                      </Text>
                      <Text weight="regular" size="xSmall" color="light">
                        {t('mfa.authenticator.step1Body')}
                      </Text>
                    </div>
                    <Stack
                      gutter={3}
                      onClick={handleCopy}
                      className={classes.pointer}
                    >
                      <FontIcon
                        color="light"
                        name={copied ? 'success-small' : 'txt-copy'}
                        showTitle={false}
                        size="medium"
                      />
                      <Text weight="semibold" size="small" color="light">
                        {secret}
                      </Text>
                      {copied && (
                        <Text className={classes.copied}>
                          {t('mfa.authenticator.copied', 'Copied!')}
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="column" gutter={18} align="flex-start">
                  <div>
                    <Text weight="medium" size="small" color="light">
                      {t('mfa.authenticator.step2Title')}
                    </Text>
                    <Text weight="regular" size="xSmall" color="light">
                      {t('mfa.authenticator.step2Body')}
                    </Text>
                  </div>
                  <TextInput
                    ref={inputRef}
                    placeholder="_ _ _ _ _ _"
                    input={{
                      value: code,
                      onChange: e =>
                        setCode(
                          e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                        )
                    }}
                    autoFocus
                  />
                </Stack>
              </Stack>

              <AuthFormFooter>
                <Button
                  type="submit"
                  label={t('mfa.buttons.confirm')}
                  loading={loading}
                  onClick={handleConfirm}
                  disabled={!code || code.length !== 6}
                />
              </AuthFormFooter>
            </form>
          </Card>
        </Column>
      </Row>
    </>
  )
}

export default MfaAuthenticatorSetupPage
